import type { ColDef } from 'ag-grid-community';
import type { RowObject } from '@highcharts/dashboards/es-modules/Data/DataTable.js';
import { DASHBOARD_QUERY_CATALOG } from '@h20/contracts/types/api/dashboard/DashboardQueryCatalog';
import type { DashboardQueryKey } from '@h20/contracts/types/api/dashboard/DashboardQueryCatalog';
import type { DashboardQueryColumn } from '@h20/contracts/types/api/dashboard/DashboardData';
import type { DashboardColumnSettings } from './column-definition';
import { GRID_COLUMN_REGISTRY } from './column-registry';

/** Builds one query's columns in catalogue order, then another column each time a column file repeats a key. */
export function buildGridColumns(
    queryKey: DashboardQueryKey,
    presentKeys: readonly string[] | undefined,
    /** Turns an i18n key from the query catalogue or a column file into the header the viewer reads. */
    translate: (i18nKey: string) => string
): ColDef<RowObject>[] {
    const settingsByKey = new Map<string, DashboardColumnSettings>();
    const extraColumns: { columnKey: string; settings: DashboardColumnSettings }[] = [];
    for (const definition of GRID_COLUMN_REGISTRY[queryKey] ?? []) {
        if (settingsByKey.has(definition.columnKey)) {
            extraColumns.push(definition);
        } else {
            settingsByKey.set(definition.columnKey, definition.settings);
        }
    }

    const declaredColumns: readonly DashboardQueryColumn[] =
        DASHBOARD_QUERY_CATALOG[queryKey].columns;
    const columnsInRows = declaredColumns.filter(
        (column) => presentKeys === undefined || presentKeys.includes(column.key)
    );
    const columnsToBuild = columnsInRows.map((catalogueColumn) => ({
        catalogueColumn,
        colId: catalogueColumn.key,
        settings: settingsByKey.get(catalogueColumn.key) ?? {},
    }));
    for (const [index, extraColumn] of extraColumns.entries()) {
        const catalogueColumn = columnsInRows.find(
            (column) => column.key === extraColumn.columnKey
        );
        if (catalogueColumn !== undefined) {
            columnsToBuild.push({
                catalogueColumn,
                colId: `${catalogueColumn.key}:${index}`,
                settings: extraColumn.settings,
            });
        }
    }

    return columnsToBuild.map(({ catalogueColumn, colId, settings }) => ({
        floatingFilter: Boolean(settings.filter),
        ...settings,
        headerName: translate(settings.headerName ?? catalogueColumn.label),
        field: catalogueColumn.key,
        colId,
    }));
}
